import { useCallback, useContext, useEffect } from 'react';
import {
  To,
  UNSAFE_NavigationContext as NavigationContext,
} from 'react-router-dom';
import { useModal } from '../useModalV2';

/**
 * navigate("/dashboard") - simple path string
 * (instead of  navigate({ pathname: "/dashboard", search: "?tab=1" }) - path with query/hash)
 */
type StringPath = string;

function isStringPath(destination: To): destination is StringPath {
  return typeof destination === 'string';
}

function isGoingToAllowedUrl(
  destination: To,
  allowNavigationWithinSubPagesUrlPath?: string,
): boolean {
  if (!allowNavigationWithinSubPagesUrlPath) {
    return false;
  }
  const destinationPath = isStringPath(destination)
    ? destination
    : destination.pathname ?? '';
  return (
    destinationPath.startsWith(allowNavigationWithinSubPagesUrlPath) &&
    destinationPath !== allowNavigationWithinSubPagesUrlPath
  );
}

// Covers the cases that React Router does not catch,
// including navigating to backbone, refresh, and the address bar.
// Uses the native browser modal
export const usePreventNavigationFallback = (requiresConfirmation: boolean) => {
  useEffect(() => {
    if (!requiresConfirmation) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault(); // triggers browser dialog
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true; // legacy fallback
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [requiresConfirmation]);
};

// TODO: Switch to using useBlocker once we've migrated to a data router
// https://reactrouter.com/6.30.1/routers/picking-a-router#using-v64-data-apis
export function usePreventNavigation(
  requiresConfirmation: boolean,
  {
    title,
    description,
    allowNavigationWithinSubPagesUrlPath,
  }: {
    title: string;
    description: string;
    /** to e.g. allow navigating between different pages of one wizard, but alert when navigating away */
    allowNavigationWithinSubPagesUrlPath?: string;
  },
) {
  usePreventNavigationFallback(requiresConfirmation);

  const { navigator } = useContext(NavigationContext);

  const { confirm } = useModal();
  const confirmNavigation = useCallback(
    () =>
      confirm({
        title,
        description,
        cancelButtonText: 'No',
        confirmButtonText: 'Yes',
        danger: true,
      }),
    [confirm, description, title],
  );

  useEffect(() => {
    const { push } = navigator;
    type PushFunction = typeof push;

    if (!requiresConfirmation) {
      // Restore if requiresConfirmation became false
      navigator.push = push;
      return undefined;
    }

    const blockNavigationWrapper =
      (originalMethod: PushFunction): PushFunction =>
      (...args: Parameters<PushFunction>) => {
        const destination = args[0];
        if (
          isGoingToAllowedUrl(destination, allowNavigationWithinSubPagesUrlPath)
        ) {
          originalMethod(...args);
          return;
        }

        void confirmNavigation().then((confirmed) => {
          if (confirmed) {
            originalMethod(...args);
          }
        });
      };

    navigator.push = blockNavigationWrapper(push);

    return () => {
      // Restore on navigating away
      navigator.push = push;
    };
  }, [
    navigator,
    confirmNavigation,
    requiresConfirmation,
    allowNavigationWithinSubPagesUrlPath,
  ]);
}
